import { type CommunicationFormValues } from "@repo/api";
import { useCallback, useEffect, useRef } from "react";
import { type UseFormReturn } from "react-hook-form";

import { getDraftKey } from "./utils";

interface Props {
  opportunityId: number;
  form: UseFormReturn<CommunicationFormValues>;
  isOpen: boolean;
  emptyValues: CommunicationFormValues;
}

export function useCommunicationDraft({
  opportunityId,
  form,
  isOpen,
  emptyValues,
}: Props) {
  const draftKey = getDraftKey(opportunityId);
  const isDraftRestoredRef = useRef(false);

  const { reset } = form;

  useEffect(() => {
    if (!isOpen) {
      isDraftRestoredRef.current = false;
      reset(emptyValues);
      return;
    }

    const raw = localStorage.getItem(draftKey);
    if (!raw) {
      reset(emptyValues);
      isDraftRestoredRef.current = false;
      return;
    }

    try {
      reset(JSON.parse(raw));
      isDraftRestoredRef.current = true;
    } catch {
      localStorage.removeItem(draftKey);
      reset(emptyValues);
      isDraftRestoredRef.current = false;
    }
  }, [isOpen, draftKey, reset, emptyValues]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let timeout: ReturnType<typeof setTimeout> | undefined;
    const subscription = form.watch((values) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const hasContent =
          (values.toRecipients?.length ?? 0) > 0 ||
          (values.ccRecipients?.length ?? 0) > 0 ||
          Boolean(values.subject) ||
          Boolean(values.body) ||
          (values.attachments?.length ?? 0) > 0;

        if (hasContent) {
          localStorage.setItem(draftKey, JSON.stringify(values));
        } else {
          localStorage.removeItem(draftKey);
        }
      }, 500);
    });

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [form, isOpen, draftKey]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(draftKey);
    reset(emptyValues);
    isDraftRestoredRef.current = false;
  }, [draftKey, reset, emptyValues]);

  return { isDraftRestoredRef, clearDraft };
}
